import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, viewChild } from '@angular/core';
import { TransferVehicleResponseDto, UpdateVehicleDto, VehicleImageResponseDto, VehicleResponseDto, VehicleService, VehicleWithImagesResponseDto } from '../../services/vehicle.service';
import { StatefulComponent } from '../../components/stateful/stateful.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { NgbCarousel, NgbCarouselConfig, NgbCarouselModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { VehicleFormFieldsComponent } from '../../components/vehicle-form-fields/vehicle-form-fields.component';
import { buildVehicleForm, vehicleFormPatchValue } from '../../components/vehicle-form-fields/vehicle-form';
import { showLoadingMaskAction } from '../../ngrx/actions/global.actions';
import * as GarageActions from '../../ngrx/actions/garage.actions';
import { selectVehicleDetailById } from '../../ngrx/app.state';
import { AlertService } from '../../services/alert.service';
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component';
import { TransferVehicleModalComponent } from '../../components/transfer-vehicle-modal/transfer-vehicle-modal.component';
import { InvoiceService, VehicleServiceHistoryDto, VehicleServiceHistoryItemDto } from '../../services/invoice.service';

@Component({
  selector: 'app-vehicle-detail',
  imports: [CommonModule, NgbCarouselModule, RouterLink, VehicleFormFieldsComponent, FormsModule, ReactiveFormsModule],
  templateUrl: './vehicle-detail.component.html',
  styleUrl: './vehicle-detail.component.scss'
})
export class VehicleDetailComponent extends StatefulComponent implements OnInit {

  mainImageSrc: string = '';
  primaryImageId: string = '';
  primaryImageUpdate: VehicleImageResponseDto | null = null;
  vehicleDetails: VehicleWithImagesResponseDto | undefined;
  vehicleThumbPages: VehicleImageResponseDto[][] = [];
  vehicleThumbPageSize = 6;
  isEditing = false;
  vehicleDetailsForm: FormGroup = new FormGroup({});
  filesForUpload: File[] = [];
  filesForDeletion: Map<string, VehicleImageResponseDto> = new Map<string, VehicleImageResponseDto>;
  imageUploadPreviews: { file: File; previewUrl: string }[] = [];

  // Completed invoices for this vehicle (service history), grouped by invoice.
  serviceHistory: VehicleServiceHistoryDto[] = [];
  historyLoading = false;
  historyError = '';

  constructor(
    private vehicleService: VehicleService,
    private route: ActivatedRoute,
    private router: Router,
    private config: NgbCarouselConfig,
    private alertService: AlertService,
    private ngbModal: NgbModal,
    private invoiceService: InvoiceService
  ) {
    super();
    config.interval = 0;
    config.wrap = true;
  }

  ngOnInit(): void {
    this.subs.push(
      this.route.params.subscribe((params: any) => {
        const vehicleId = params.vehicleId || '';

        // Completed work items for this vehicle (independent of the NgRx
        // vehicle-detail load).
        this.loadServiceHistory(vehicleId);

        // Subscribe to vehicle details for this ID
        this.subs.push(
          this.select(selectVehicleDetailById(vehicleId)).subscribe({
            next: (vehicleDetails) => {
              if (vehicleDetails) {
                this.mainImageSrc = this.getPrimaryImageUrl(vehicleDetails.VehicleImages);
                this.vehicleDetails = vehicleDetails;
                this.vehicleThumbPages = this.parseVehicleThumbs(vehicleDetails.VehicleImages);
                this.initializeVehicleDetailsForm(vehicleDetails);

                // Update the vehicle in the list cache to keep it in sync
                this.dispatch(GarageActions.updateVehicleInList({ vehicle: vehicleDetails }));
              } else {
                // Only dispatch if vehicle details don't exist yet
                this.dispatch(GarageActions.loadVehicleDetail({ vehicleId }));
              }
            }
          })
        );
      })
    );
  }

  private loadServiceHistory(vehicleId: string): void {
    if (!vehicleId) return;
    this.historyLoading = true;
    this.historyError = '';
    this.subs.push(
      this.invoiceService.getCompletedWorkItemsByVehicle(vehicleId).subscribe({
        next: (history) => {
          this.serviceHistory = history;
          this.historyLoading = false;
        },
        error: (error) => {
          console.error('Failed to load service history:', error);
          this.historyError = 'Failed to load service history.';
          this.historyLoading = false;
        }
      })
    );
  }

  mechanicName(item: VehicleServiceHistoryItemDto): string {
    return `${item.EmpFirstName ?? ''} ${item.EmpLastName ?? ''}`.trim() || 'Unassigned';
  }

  openInvoicePdf(invoiceId: string): void {
    this.subs.push(this.invoiceService.getInvoicePdfUrl(invoiceId).subscribe({
      next: (res) => window.open(res.url, '_blank'),
      error: (error) => {
        console.error('Failed to open invoice PDF:', error);
        this.alertService.showSnackBar('Failed to open invoice PDF.');
      }
    }));
  }

  onImageDelete(vehicleThumb: VehicleImageResponseDto) {
    if (vehicleThumb.ImageId === this.getPrimaryOrEditImageId()) {
      this.alertService.showSnackBar(`Cannot mark primary image for deletion`);
      return;
    }
    if (this.filesForDeletion.has(vehicleThumb.ImageId)) {
      this.filesForDeletion.delete(vehicleThumb.ImageId);
    } else {
      this.filesForDeletion.set(vehicleThumb.ImageId, vehicleThumb);
    }
  }

  onThumbClick(vehicleThumb: VehicleImageResponseDto) {
    this.mainImageSrc = vehicleThumb.ImageUrl;
  }

  onPrimaryClick(vehicleThumb: VehicleImageResponseDto) {
    if ((this.primaryImageUpdate && vehicleThumb.ImageId === this.primaryImageUpdate?.ImageId) ||
        !this.primaryImageUpdate && vehicleThumb.ImageId === this.primaryImageId) {
      return;
    }
    if (this.filesForDeletion.has(vehicleThumb.ImageId)) {
      this.filesForDeletion.delete(vehicleThumb.ImageId);
    }
    this.primaryImageUpdate = vehicleThumb;
  }

  getPrimaryOrEditImageId(): string {
    if (this.primaryImageUpdate) {
      return this.primaryImageUpdate.ImageId;
    } else {
      return this.primaryImageId;
    }
  }

  parseVehicleThumbs(vehicleThumbs: VehicleImageResponseDto[]): VehicleImageResponseDto[][] {
    // Sort images so primary image is always first
    const sortedThumbs = [...vehicleThumbs].sort((a, b) => {
      if (a.IsPrimary && !b.IsPrimary) return -1;
      if (!a.IsPrimary && b.IsPrimary) return 1;
      return 0;
    });

    const pages: VehicleImageResponseDto[][] = [];

    for (let i = 0; i < sortedThumbs.length; i += this.vehicleThumbPageSize) {
      pages.push(sortedThumbs.slice(i, i + this.vehicleThumbPageSize));
    }

    return pages;
  }

  getPrimaryImageUrl(images: VehicleImageResponseDto[]): string {
    // Find the primary image, or fall back to the first image, or the shared
    // "No Image" placeholder so the img never renders as a broken link.
    const primaryImage = images.find(img => img.IsPrimary);
    this.primaryImageId = primaryImage?.ImageId || '';
    return primaryImage?.ImageUrl || images[0]?.ImageUrl || this.vehicleService.getDefaultImage();
  }

  initializeVehicleDetailsForm(vehicleDetails?: VehicleWithImagesResponseDto) {
    this.vehicleDetailsForm = buildVehicleForm(vehicleDetails);
  }

  toggleIsEditing() {
    this.isEditing = !this.isEditing;
  }

  onSubmitVehicleEdits() {
    // don't let them submit if the form has validation errors
    if (!this.vehicleDetailsForm.valid) {
      for (let control in this.vehicleDetailsForm.controls) {
        if (!this.vehicleDetailsForm.get(control)?.valid) {
          this.vehicleDetailsForm.get(control)?.markAsDirty();
          this.alertService.showSnackBar(`Error with ${control} input`);
          return;
        }
      }
      // Group-level errors: "Other…" selected without its free-text filled in.
      if (this.vehicleDetailsForm.errors?.['makeOtherRequired']) {
        this.alertService.showSnackBar('Please enter the vehicle make');
      } else if (this.vehicleDetailsForm.errors?.['modelOtherRequired']) {
        this.alertService.showSnackBar('Please enter the vehicle model');
      }
      return;
    }

    this.dispatch(showLoadingMaskAction({ showLoadingMask: true }));

    // Build array of deletion observables - use Map.values() or Map.keys()
    const imageDeletionSubs: Observable<any>[] = [];
    for (const [imageId] of this.filesForDeletion) {
      imageDeletionSubs.push(this.vehicleService.deleteVehicleImage(imageId));
    }

    // Build array of upload observables
    const imageUploadSubs: Observable<any>[] = [];
    for (const file of this.filesForUpload) {
      imageUploadSubs.push(this.vehicleService.uploadVehicleImage(this.vehicleDetails!.VehicleId, file));
    }

    // Build update observable if form data changed. Blank VIN/plate are sent
    // as '' so the API treats them as an explicit clear.
    const updateData: UpdateVehicleDto = vehicleFormPatchValue(this.vehicleDetailsForm);

    const hasFormChanges = this.isFormDataDifferent(updateData);
    const hasImageChanges = imageDeletionSubs.length > 0 || imageUploadSubs.length > 0;
    const hasPrimaryImageChange = this.primaryImageUpdate !== null && this.primaryImageUpdate.ImageId !== this.primaryImageId;

    // If nothing changed, exit early
    if (!hasFormChanges && !hasImageChanges && !hasPrimaryImageChange) {
      this.isEditing = false;
      this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
      return;
    }

    // Combine all operations into a single pipeline
    const allOperations: Observable<any>[] = [];

    if (imageDeletionSubs.length > 0) {
      allOperations.push(forkJoin(imageDeletionSubs));
    }

    if (imageUploadSubs.length > 0) {
      allOperations.push(forkJoin(imageUploadSubs));
    }

    if (hasFormChanges && this.vehicleDetails?.VehicleId) {
      allOperations.push(this.vehicleService.updateVehicle(this.vehicleDetails.VehicleId, updateData));
    }

    // Handle primary image update
    if (this.primaryImageUpdate && this.primaryImageUpdate.ImageId !== this.primaryImageId) {
      // Update the old primary image to false if it exists and is not being deleted
      if (this.primaryImageId && !this.filesForDeletion.has(this.primaryImageId)) {
        allOperations.push(this.vehicleService.updateVehicleImage(this.primaryImageId, { IsPrimary: false }));
      }

      // Update the new primary image to true
      allOperations.push(this.vehicleService.updateVehicleImage(this.primaryImageUpdate.ImageId, { IsPrimary: true }));
    }

    // Create the base observable - either forkJoin of operations or just of(null)
    const operations$: Observable<any> = allOperations.length > 0
      ? forkJoin(allOperations)
      : of(null);

    // Execute all operations, then refresh vehicle details
    this.subs.push(
      operations$.subscribe({
        next: () => {
          // Dispatch action to reload vehicle details from API
          this.dispatch(GarageActions.loadVehicleDetail({ vehicleId: this.vehicleDetails!.VehicleId }));

          // Reset editing state
          // Clean up preview URLs
          this.imageUploadPreviews.forEach(preview => URL.revokeObjectURL(preview.previewUrl));
          this.imageUploadPreviews = [];
          this.filesForUpload = [];
          this.filesForDeletion.clear();
          this.primaryImageUpdate = null;
          this.isEditing = false;

          this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
          this.alertService.showSnackBar('Vehicle updated successfully');
        },
        error: (error: any) => {
          console.error('Update failed:', error);
          this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
          this.alertService.showSnackBar('Failed to update vehicle. Please refresh and try again.');
        }
      })
    );
  }

  onDiscardChanges(): void {
    this.initializeVehicleDetailsForm(this.vehicleDetails);

    // Clean up preview URLs
    this.imageUploadPreviews.forEach(preview => URL.revokeObjectURL(preview.previewUrl));
    this.imageUploadPreviews = [];
    this.filesForUpload = [];
    this.filesForDeletion.clear();
    this.primaryImageUpdate = null;

    this.isEditing = false;
  }

  isFormDataDifferent(updateData: UpdateVehicleDto): boolean {
    // Normalize both sides — stored values may be null where the form has ''.
    const same = (a?: string | null, b?: string | null) => (a ?? '').trim() === (b ?? '').trim();
    return !(same(this.vehicleDetails?.Make, updateData.Make) &&
        same(this.vehicleDetails?.Model, updateData.Model) &&
        same(this.vehicleDetails?.Vin, updateData.Vin) &&
        same(this.vehicleDetails?.Year, updateData.Year) &&
        same(this.vehicleDetails?.LicensePlate, updateData.LicensePlate) &&
        same(this.vehicleDetails?.PlateState, updateData.PlateState));
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      // Process all selected files
      Array.from(input.files).forEach(file => {
        // Validate the file
        const validation = this.vehicleService.validateImageFile(file);
        if (!validation.valid) {
          this.alertService.showSnackBar(validation.error || 'Invalid file');
          return;
        }

        this.addImageForUpload(file);
      });

      // Clear the input so the same files can be selected again if needed
      input.value = '';
    }
  }

  private addImageForUpload(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const previewUrl = e.target?.result as string;
      this.imageUploadPreviews.push({ file, previewUrl });
      this.filesForUpload.push(file);
    };
    reader.readAsDataURL(file);
  }

  onRemoveUploadPreview(index: number): void {
    // Revoke the preview URL to free up memory
    if (this.imageUploadPreviews[index]) {
      URL.revokeObjectURL(this.imageUploadPreviews[index].previewUrl);
    }

    // Remove from both arrays
    this.imageUploadPreviews.splice(index, 1);
    this.filesForUpload.splice(index, 1);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      // Process all dropped files
      Array.from(files).forEach(file => {
        // Validate the file
        const validation = this.vehicleService.validateImageFile(file);
        if (!validation.valid) {
          this.alertService.showSnackBar(validation.error || 'Invalid file');
          return;
        }

        this.addImageForUpload(file);
      });
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  uploadImage(vehicleId: string, file: File): void {
    this.subs.push(this.vehicleService.uploadVehicleImage(vehicleId, file).subscribe({
      next: (response) => {
        this.alertService.showSnackBar('Image uploaded successfully');
        
        // Add new image to vehicle
        if (this.vehicleDetails) {
          this.vehicleDetails.VehicleImages.push(response);
        }
      },
      error: (error) => {
        console.error('Upload failed:', error);
        this.alertService.showSnackBar('Failed to upload image. Please try again.');
      }
    }));
  }

  // Moving a vehicle between garages is a shop-side repair, not something a
  // customer may do to their own car.
  get canTransferVehicle(): boolean {
    return this.permissions?.includes('EDIT_ALL_GARAGES') || false;
  }

  onTransferVehicleClick(): void {
    if (!this.vehicleDetails) {
      return;
    }

    const modalRef = this.ngbModal.open(TransferVehicleModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.vehicle = this.vehicleDetails;

    this.subs.push(
      modalRef.componentInstance.transferred.subscribe((response: TransferVehicleResponseDto) => {
        // Ownership drives both the garage list and the cached detail entries,
        // so drop the whole cache rather than patching one vehicle. The detail
        // subscription re-fetches on the next emission.
        this.dispatch(GarageActions.clearVehicles());
        this.isEditing = false;

        if (response.VehicleId !== this.vehicleDetails?.VehicleId) {
          // Merged away — follow the surviving vehicle.
          this.router.navigate(['/my-garage', response.VehicleId]);
        }
      })
    );

    modalRef.result.catch(() => {
      // Modal dismissed - do nothing
    });
  }

  onDeleteVehicleClick(): void {
    if (!this.vehicleDetails) {
      return;
    }

    const vehicleName = this.getVehicleDisplayName(this.vehicleDetails);

    const modalRef = this.ngbModal.open(ConfirmationModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.title = 'Delete Vehicle';
    modalRef.componentInstance.message = `Are you sure you want to delete ${vehicleName}? This action cannot be undone and will remove all associated images.`;
    modalRef.componentInstance.confirmButtonText = 'Yes, Delete';
    modalRef.componentInstance.cancelButtonText = 'Cancel';
    modalRef.componentInstance.confirmButtonClass = 'btn-danger';

    modalRef.result.then((confirmed) => {
      if (confirmed && this.vehicleDetails) {
        this.dispatch(showLoadingMaskAction({ showLoadingMask: true }));

        this.subs.push(
          this.vehicleService.deleteVehicle(this.vehicleDetails.VehicleId).subscribe({
            next: () => {
              this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
              this.alertService.showSnackBar('Vehicle deleted successfully');
              this.router.navigate(['/my-garage']);
            },
            error: (error) => {
              console.error('Delete failed:', error);
              this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
              this.alertService.showSnackBar('Failed to delete vehicle. Please try again.');
            }
          })
        );
      }
    }).catch(() => {
      // Modal dismissed - do nothing
    });
  }

  private getVehicleDisplayName(vehicle: VehicleWithImagesResponseDto): string {
    const parts = [];
    if (vehicle.Year) parts.push(vehicle.Year);
    if (vehicle.Make) parts.push(vehicle.Make);
    if (vehicle.Model) parts.push(vehicle.Model);

    return parts.length > 0 ? parts.join(' ') : 'this vehicle';
  }
}
